import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  HeatmapLayerF,
  Polygon,
  InfoWindow,
} from "@react-google-maps/api";
import { usStatesShape } from "../../lib/utils";
import { Link } from "react-router-dom";

interface GoogleHeatmapProps {
  heatmapData?: {
    location: google.maps.LatLng;
    weight: number;
    state: string;
  }[];
  onStateClick?: (state: string) => void;
}

const containerStyle = {
  width: "100%",
  height: "100vh",
};

const center = {
  lat: 37.0902, // Center of the US
  lng: -95.7129,
};

const GoogleHeatmap: React.FC<GoogleHeatmapProps> = ({
  heatmapData,
  onStateClick,
}) => {
  const [stateWeights, setStateWeights] = useState<{ [key: string]: number }>(
    {}
  );
  const [tooltip, setTooltip] = useState<{
    state: string;
    clients: number;
    position: { lat: number; lng: number };
  } | null>(null);

  // ✅ Keep a reference to prevent re-computation
  const stateWeightsRef = useRef<{ [key: string]: number }>({});

  /** ✅ Compute state weights when heatmapData changes */
  useEffect(() => {
    if (!heatmapData || heatmapData.length === 0) {
      stateWeightsRef.current = {};
      setStateWeights({});
      return;
    }

    const weightMap: { [key: string]: number } = {};

    heatmapData.forEach((point) => {
      if (point && point.state) {
        weightMap[point.state] = (weightMap[point.state] || 0) + point.weight;
      }
    });

    // Always update when heatmapData changes
    stateWeightsRef.current = weightMap;
    setStateWeights(weightMap);
  }, [heatmapData]);

  /** ✅ Show Tooltip without resetting colors */
  const handleStateClick = useCallback(
    (stateName: string, position: { lat: number; lng: number }) => {
      setTooltip({
        state: stateName,
        clients: stateWeightsRef.current[stateName] || 0,
        position,
      });

      if (onStateClick) {
        onStateClick(stateName);
      }
    },
    [onStateClick]
  );

  /** ✅ Keeps colors even after clicking */
  const getStateColor = (stateName: string) => {
    const weight = stateWeightsRef.current[stateName] || 0;
    const weights = Object.values(stateWeightsRef.current);
    const minWeight = weights.length ? Math.min(...weights) : 0;
    const maxWeight = weights.length ? Math.max(...weights) : 1;

    if (weight === 0) {
      return "#B3E5FC"; // Light blue for states with no clients
    }

    const intensity = maxWeight > minWeight ? (weight - minWeight) / (maxWeight - minWeight) : 0;
    const r = 255;
    const g = Math.round(200 - intensity * 200);
    const b = Math.round(200 - intensity * 200);

    const color = `rgb(${r},${g},${b})`;
    return color;
  };

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={5}
      options={{
        mapTypeId: "roadmap",
        streetViewControl: false,
        fullscreenControl: false,
      }}
    >
      {/* {heatmapData && (
        <HeatmapLayerF
          data={heatmapData.map((point) => point.location)} // ✅ Only use locations
          options={{
            radius: 50, // 🔥 Increase radius to blend colors better
            opacity: 0.6, // ✅ Keep opacity
            dissipating: true, // ✅ Helps fade points instead of clustering them
          }}
        />
      )} */}

      {usStatesShape().features.map((state) => {
        const stateName = state.properties.name;
        const coordinates = state.geometry.coordinates;

        return coordinates.map((polygon: any[], polygonIndex: number) => {
          if (typeof polygon[0][0] === "number") {
            const path = polygon.map(([lng, lat]: [number, number]) => ({
              lat,
              lng,
            }));

            return (
              <Polygon
                key={`${stateName}-${polygonIndex}`}
                path={path}
                options={{
                  fillColor: getStateColor(stateName),
                  fillOpacity: 0.7,
                  strokeWeight: 1,
                  strokeColor: "#000",
                  clickable: true,
                }}
                onClick={(e) =>
                  handleStateClick(stateName, {
                    lat: e.latLng?.lat() || center.lat,
                    lng: e.latLng?.lng() || center.lng,
                  })
                }
              />
            );
          }

          return polygon.map((subPolygon: any[], subIndex: number) => {
            const path = subPolygon.map(([lng, lat]: [number, number]) => ({
              lat,
              lng,
            }));

            return (
              <Polygon
                key={`${stateName}-${polygonIndex}-${subIndex}`}
                path={path}
                options={{
                  fillColor: getStateColor(stateName),
                  fillOpacity: 0.7,
                  strokeWeight: 1,
                  strokeColor: "#000",
                  clickable: true,
                }}
                onClick={(e) =>
                  handleStateClick(stateName, {
                    lat: e.latLng?.lat() || center.lat,
                    lng: e.latLng?.lng() || center.lng,
                  })
                }
              />
            );
          });
        });
      })}

      {tooltip && (
        <InfoWindow
          position={tooltip.position}
          onCloseClick={() => setTooltip(null)}
        >
          <div
            style={{
              minWidth: "100px",
              padding: "4px",
              fontSize: "11px",
              lineHeight: "1.2",
            }}
          >
            <strong>{tooltip.state}</strong>
            <br />
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
              Clients: {tooltip.clients}
            </span>
            <br />
            <Link to={`/clients/filter?state=${tooltip.state}`}>
              See client(s)
            </Link>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default GoogleHeatmap;
